import { Controller } from "@hotwired/stimulus"
import { DisconnectReason, Room, RoomEvent, Track, TrackEvent } from "livekit-client"
import { lobbyMarkup, lobbySignature, newcomers, waitingIds } from "../calls/lobby"
import { shouldWaitForHost } from "../calls/presence"
import { chatPayload, parseChatMessage } from "../calls/session"
import { featuredIdentity, initials, tileMedia } from "../calls/stage"

// Prejoin preview, tiles, screen share and in-call chat. Tokens come from our server.
export default class extends Controller {
  static targets = [
    "status", "lobby", "prejoin", "live", "tiles", "chatLog", "chatInput",
    "mic", "cam", "share", "preview", "previewAvatar", "chat", "chatButton",
    "people", "peopleButton", "lobbyList", "left", "away",
  ]
  static values = {
    tokenUrl: String,
    livekitUrl: String,
    admitted: Boolean,
    selfName: String,
    role: String,
    hostIdentity: String,
    lobbyUrl: String,
    admitUrl: String,
    admitLabel: String,
    lobbyEmpty: String,
    lobbySeed: String,
  }

  connect() {
    this.micOn = true
    this.camOn = true
    this.sharing = false
    this.intentionalLeave = false
    this.waitingForHost = false
    this.previewGeneration = 0
    this.restartBound = new WeakSet()
    this.syncDeviceButtons()
    this.rememberLobby(this.parseLobbySeed())
    if (this.hasPreviewAvatarTarget) this.previewAvatarTarget.textContent = initials(this.selfNameValue)
    if (this.lobbyUrlValue) {
      this.pollLobby()
      this.lobbyPoll = window.setInterval(() => this.pollLobby(), 3000)
    }
    if (this.admittedValue) {
      this.startPreview()
      return
    }
    this.poll = window.setInterval(() => this.pollAdmission(), 3000)
  }

  disconnect() {
    window.clearInterval(this.poll)
    window.clearInterval(this.lobbyPoll)
    this.stopPreview()
    this.room?.disconnect()
  }

  async enter(event) {
    event.preventDefault()
    const response = await this.fetchToken()
    if (!response.ok) {
      await this.noteDenial(response)
      return
    }
    this.stopHostWatch()
    const body = await response.json()
    await this.connectRoom(body.url || this.livekitUrlValue, body.token)
  }

  async pollAdmission() {
    const response = await this.fetchToken()
    if (!response.ok) {
      await this.noteHostAbsent(response)
      return
    }
    this.stopHostWatch()
    this.waitingForHost = false
    this.revealPrejoin()
  }

  async pollLobby() {
    if (!this.lobbyUrlValue || !this.hasLobbyListTarget) return
    let response
    try {
      response = await fetch(this.lobbyUrlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
    } catch {
      return
    }
    if (!response.ok || !this.element.isConnected || !this.hasLobbyListTarget) return
    let body
    try {
      body = await response.json()
    } catch {
      return
    }
    if (!this.element.isConnected || !this.hasLobbyListTarget) return
    const people = Array.isArray(body.waiting) ? body.waiting : []
    this.applyLobby(people)
  }

  applyLobby(people) {
    const arrived = newcomers(this.seenLobbyIds, people)
    const signature = lobbySignature(people)
    this.seenLobbyIds = waitingIds(people)
    if (signature !== this.lobbySignature) {
      this.lobbySignature = signature
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content || ""
      this.lobbyListTarget.innerHTML = lobbyMarkup(people, {
        admitUrl: this.admitUrlValue,
        admitLabel: this.admitLabelValue,
        emptyLabel: this.lobbyEmptyValue,
        csrf,
      })
    }
    if (arrived.length && this.hasPeopleTarget) this.setPeopleOpen(true)
  }

  parseLobbySeed() {
    if (!this.hasLobbySeedValue || !this.lobbySeedValue) return []
    try {
      const parsed = JSON.parse(this.lobbySeedValue)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  rememberLobby(people) {
    this.seenLobbyIds = waitingIds(people)
    this.lobbySignature = lobbySignature(people)
  }

  toggleMic(event) {
    event.preventDefault()
    this.micOn = !this.micOn
    this.syncDeviceButtons()
    if (this.room) {
      this.room.localParticipant.setMicrophoneEnabled(this.micOn)
    } else {
      this.startPreview()
    }
  }

  toggleCam(event) {
    event.preventDefault()
    this.camOn = !this.camOn
    this.syncDeviceButtons()
    if (this.room) {
      this.room.localParticipant.setCameraEnabled(this.camOn).catch(() => {
        this.camOn = false
        this.syncDeviceButtons()
      })
    } else {
      this.startPreview()
    }
  }

  toggleShare(event) {
    event.preventDefault()
    this.sharing = !this.sharing
    if (this.hasShareTarget) this.shareTarget.setAttribute("aria-pressed", String(this.sharing))
    this.room?.localParticipant.setScreenShareEnabled(this.sharing).catch(() => {
      this.sharing = false
      if (this.hasShareTarget) this.shareTarget.setAttribute("aria-pressed", "false")
    })
  }

  toggleChat(event) {
    event.preventDefault()
    if (!this.hasChatTarget) return
    this.chatTarget.hidden = !this.chatTarget.hidden
    const open = !this.chatTarget.hidden
    if (this.hasChatButtonTarget) this.chatButtonTarget.setAttribute("aria-expanded", String(open))
    if (open && this.hasPeopleTarget) this.setPeopleOpen(false)
  }

  togglePeople(event) {
    event.preventDefault()
    if (!this.hasPeopleTarget) return
    this.setPeopleOpen(this.peopleTarget.hidden)
    if (!this.peopleTarget.hidden && this.hasChatTarget) {
      this.chatTarget.hidden = true
      if (this.hasChatButtonTarget) this.chatButtonTarget.setAttribute("aria-expanded", "false")
    }
  }

  leave(event) {
    event.preventDefault()
    this.intentionalLeave = true
    this.stopHostWatch()
    this.stopPreview()
    const room = this.room
    this.room = null
    room?.disconnect()
    if (this.hasLiveTarget) this.liveTarget.hidden = true
    if (this.hasPrejoinTarget) this.prejoinTarget.hidden = true
    if (this.hasLobbyTarget) this.lobbyTarget.hidden = true
    if (this.hasAwayTarget) this.awayTarget.hidden = true
    if (this.hasChatTarget) this.chatTarget.hidden = true
    if (this.hasPeopleTarget) this.peopleTarget.hidden = true
    if (this.hasLeftTarget) this.leftTarget.hidden = false
  }

  rejoin(event) {
    event.preventDefault()
    this.intentionalLeave = false
    this.waitingForHost = false
    this.revealPrejoin()
  }

  sendChat(event) {
    event.preventDefault()
    if (!this.hasChatInputTarget || !this.room) return
    const payload = chatPayload(this.selfNameValue, this.chatInputTarget.value)
    if (!payload) return
    const bytes = new TextEncoder().encode(payload)
    this.room.localParticipant.publishData(bytes, { reliable: true })
    this.appendChat(parseChatMessage(payload))
    this.chatInputTarget.value = ""
  }

  async fetchToken() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    return fetch(this.tokenUrlValue, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": token || "",
      },
    })
  }

  async connectRoom(url, token) {
    this.previewGeneration += 1
    await this.stopPreview()
    if (this.hasPrejoinTarget) this.prejoinTarget.hidden = true
    if (this.hasLobbyTarget) this.lobbyTarget.hidden = true
    if (this.hasLiveTarget) this.liveTarget.hidden = false
    this.room = new Room()
    this.room.on(RoomEvent.ParticipantConnected, (participant) => this.ensureTile(participant))
    this.room.on(RoomEvent.ParticipantDisconnected, (participant) => {
      this.tileFor(participant.identity)?.remove()
      this.layoutTiles()
      this.holdIfHostLeft(participant.identity)
    })
    this.room.on(RoomEvent.Disconnected, (reason) => {
      if (reason !== DisconnectReason.PARTICIPANT_REMOVED && reason !== DisconnectReason.ROOM_DELETED) return
      this.holdIfHostLeft(this.hostIdentityValue)
    })
    this.room.on(RoomEvent.TrackSubscribed, (track, _publication, participant) => {
      this.attach(track, participant)
    })
    this.room.on(RoomEvent.TrackUnsubscribed, (track) => {
      const tiles = new Set()
      track.detach().forEach((element) => {
        const tile = element.closest(".call-tile")
        if (tile) tiles.add(tile)
        element.remove()
      })
      tiles.forEach((tile) => this.syncTile(tile))
      this.layoutTiles()
    })
    this.room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
      if (publication.track) this.attach(publication.track, participant)
    })
    this.room.on(RoomEvent.LocalTrackUnpublished, (publication) => {
      publication.track?.detach().forEach((element) => element.remove())
      this.refreshTiles()
    })
    this.room.on(RoomEvent.TrackMuted, (publication) => this.markMuted(publication, true))
    this.room.on(RoomEvent.TrackUnmuted, (publication) => this.markMuted(publication, false))
    this.room.on(RoomEvent.DataReceived, (payload) => {
      const raw = new TextDecoder().decode(payload)
      const message = parseChatMessage(raw)
      if (message) this.appendChat(message)
    })
    await this.room.connect(url, token)
    this.ensureTile(this.room.localParticipant)
    this.room.remoteParticipants.forEach((participant) => this.ensureTile(participant))
    await this.room.localParticipant.setMicrophoneEnabled(this.micOn)
    try {
      await this.room.localParticipant.setCameraEnabled(this.camOn)
    } catch {
      this.camOn = false
      this.syncDeviceButtons()
    }
    this.resumeVideos()
  }

  async startPreview() {
    if (!this.hasPreviewTarget) return
    this.previewGeneration += 1
    const generation = this.previewGeneration
    await this.stopPreview()
    if (generation !== this.previewGeneration) return
    if (!this.camOn) this.showPreviewAvatar()
    if (!this.micOn && !this.camOn) return
    if (!navigator.mediaDevices?.getUserMedia) {
      this.showPreviewAvatar()
      return
    }
    try {
      this.previewStream = await navigator.mediaDevices.getUserMedia({
        audio: this.micOn,
        video: this.camOn,
      })
      if (generation !== this.previewGeneration) {
        this.previewStream.getTracks().forEach((track) => track.stop())
        this.previewStream = null
        return
      }
      this.previewTarget.srcObject = this.previewStream
      this.previewTarget.classList.toggle("is-covered", !this.camOn)
      if (this.hasPreviewAvatarTarget) this.previewAvatarTarget.hidden = this.camOn
      this.resumeVideos()
    } catch {
      this.showPreviewAvatar()
    }
  }

  async stopPreview() {
    const tracks = this.previewStream?.getTracks() ?? []
    tracks.forEach((track) => track.stop())
    this.previewStream = null
    if (this.hasPreviewTarget) this.previewTarget.srcObject = null
    await Promise.all(tracks.map((track) => {
      if (track.readyState === "ended") return Promise.resolve()
      return new Promise((resolve) => {
        track.addEventListener("ended", resolve, { once: true })
        window.setTimeout(resolve, 300)
      })
    }))
  }

  resumeVideos() {
    this.element.querySelectorAll("video").forEach((video) => {
      video.playsInline = true
      video.play?.().catch(() => {})
    })
  }

  showPreviewAvatar() {
    if (this.hasPreviewTarget) this.previewTarget.classList.add("is-covered")
    if (this.hasPreviewAvatarTarget) this.previewAvatarTarget.hidden = false
  }

  syncDeviceButtons() {
    this.micTargets.forEach((button) => this.paintDevice(button, this.micOn))
    this.camTargets.forEach((button) => this.paintDevice(button, this.camOn))
  }

  paintDevice(button, enabled) {
    button.classList.toggle("is-off", !enabled)
    button.setAttribute("aria-pressed", String(!enabled))
  }

  setPeopleOpen(open) {
    this.peopleTarget.hidden = !open
    this.peopleButtonTargets.forEach((button) => button.setAttribute("aria-expanded", String(open)))
  }

  ensureTile(participant) {
    const existing = this.tileFor(participant.identity)
    if (existing) return existing

    const tile = document.createElement("article")
    tile.className = "call-tile"
    tile.dataset.identity = participant.identity

    const media = document.createElement("div")
    media.className = "call-tile__media"

    const avatar = document.createElement("div")
    avatar.className = "call-tile__avatar"
    avatar.textContent = initials(participant.name || participant.identity)

    const name = document.createElement("span")
    name.className = "call-tile__name"
    name.textContent = participant.name || participant.identity

    tile.append(media, avatar, name)
    this.tilesTarget.append(tile)
    this.layoutTiles()
    return tile
  }

  attach(track, participant) {
    const tile = this.ensureTile(participant)
    if (track.sid && tile.querySelector(`[data-track-sid="${CSS.escape(track.sid)}"]`)) {
      this.syncTile(tile)
      return
    }
    const element = track.attach()
    element.dataset.muted = track.isMuted ? "true" : "false"
    element.dataset.trackSid = track.sid || ""
    element.playsInline = true
    this.bindRestart(track)
    if (track.kind === Track.Kind.Audio) {
      element.classList.add("call-tile__audio")
      tile.append(element)
      return
    }
    if (track.kind !== Track.Kind.Video) return

    const screen = track.source === Track.Source.ScreenShare
    element.dataset.screen = screen ? "1" : "0"
    element.classList.add("call-tile__video")
    tile.querySelector(".call-tile__media").append(element)
    this.syncTile(tile)
    element.play?.().catch(() => {})
  }

  bindRestart(track) {
    if (this.restartBound.has(track)) return
    this.restartBound.add(track)
    track.on(TrackEvent.Restarted, () => this.resumeVideos())
  }

  markMuted(publication, muted) {
    publication.track?.attachedElements?.forEach((element) => {
      element.dataset.muted = muted ? "true" : "false"
    })
    this.refreshTiles()
  }

  refreshTiles() {
    this.tilesTarget.querySelectorAll(".call-tile").forEach((tile) => this.syncTile(tile))
  }

  syncTile(tile) {
    const screen = tile.querySelector("[data-screen='1']")
    const cameras = [ ...tile.querySelectorAll("[data-screen='0']") ]
    const view = tileMedia({
      hasScreen: Boolean(screen),
      hasCamera: cameras.length > 0,
      cameraMuted: cameras.every((video) => video.dataset.muted === "true"),
    })
    tile.classList.toggle("call-tile--screen", view.showScreen)
    const avatar = tile.querySelector(".call-tile__avatar")
    if (avatar) avatar.hidden = !view.showAvatar
    cameras.forEach((video) => video.classList.toggle("is-covered", !view.showCamera))
    if (screen) screen.classList.remove("is-covered")
    this.layoutTiles()
  }

  layoutTiles() {
    if (!this.hasTilesTarget) return
    const tiles = [ ...this.tilesTarget.querySelectorAll(".call-tile") ].map((tile) => ({
      identity: tile.dataset.identity,
      screen: tile.classList.contains("call-tile--screen"),
    }))
    this.tilesTarget.classList.toggle("is-presenting", Boolean(featuredIdentity(tiles)))
  }

  tileFor(identity) {
    if (!this.hasTilesTarget) return null
    return this.tilesTarget.querySelector(`[data-identity="${CSS.escape(identity)}"]`)
  }

  appendChat(message) {
    if (!message || !this.hasChatLogTarget) return
    const item = document.createElement("li")
    const who = document.createElement("span")
    who.className = "call-room__who"
    who.textContent = message.sender
    const text = document.createElement("span")
    text.textContent = message.text
    item.append(who, text)
    this.chatLogTarget.append(item)
  }

  showStatus() {
    if (!this.hasStatusTarget) return
    this.statusTarget.hidden = false
    this.statusTarget.textContent = this.statusTarget.dataset.waiting || ""
  }

  async noteDenial(response) {
    if (await this.noteHostAbsent(response)) return
    this.showStatus()
  }

  async noteHostAbsent(response) {
    if (response.status !== 403) return false
    let body = {}
    try {
      body = await response.json()
    } catch {
      return false
    }
    if (body.error !== "host_absent") return false
    this.showAway()
    return true
  }

  holdIfHostLeft(leftIdentity) {
    if (!shouldWaitForHost({
      role: this.roleValue,
      leftIdentity,
      hostIdentity: this.hostIdentityValue,
      intentional: this.intentionalLeave,
    })) return
    this.holdForHost()
  }

  holdForHost() {
    if (this.waitingForHost) return
    this.waitingForHost = true
    const room = this.room
    this.room = null
    room?.disconnect()
    this.showAway()
  }

  showAway() {
    this.stopPreview()
    if (this.hasStatusTarget) this.statusTarget.hidden = true
    if (this.hasLiveTarget) this.liveTarget.hidden = true
    if (this.hasPrejoinTarget) this.prejoinTarget.hidden = true
    if (this.hasLobbyTarget) this.lobbyTarget.hidden = true
    if (this.hasLeftTarget) this.leftTarget.hidden = true
    if (this.hasChatTarget) this.chatTarget.hidden = true
    if (this.hasPeopleTarget) this.peopleTarget.hidden = true
    if (this.hasAwayTarget) this.awayTarget.hidden = false
    if (!this.poll) this.poll = window.setInterval(() => this.pollAdmission(), 3000)
  }

  revealPrejoin() {
    this.waitingForHost = false
    if (this.hasStatusTarget) this.statusTarget.hidden = true
    if (this.hasLobbyTarget) this.lobbyTarget.hidden = true
    if (this.hasAwayTarget) this.awayTarget.hidden = true
    if (this.hasLeftTarget) this.leftTarget.hidden = true
    if (this.hasPrejoinTarget) this.prejoinTarget.hidden = false
    this.startPreview()
  }

  stopHostWatch() {
    window.clearInterval(this.poll)
    this.poll = null
  }
}
